import React, { Fragment, useEffect, useRef, useState, useMemo } from 'react';
import { peep_client } from '~/src/api/peep';
import Header from '~/src/components/Header';
import { useGroups } from '~/src/api_hooks/groups';
import { useCohort } from '~/src/api_hooks/users';
import Badge from '~/src/components/Badge';
import Modal from '~/src/components/Modal';
import { Uuid, UuidString } from '~src/utils/UUID';
import { produce } from 'immer';
import Fuse, { FuseResult } from 'fuse.js';
import { useNavigate } from 'react-router-dom';
import MediaUploader, { UploadedMedia } from './MediaUploader';

interface alias {
  group_id: Uuid;
  alias: string;
}

interface SearchableCohort {
  aliases: alias[];
  user_id: UuidString;
}

export default function CreatePost() {
  const navigate = useNavigate();

  const { data: groups, isLoading: groups_loading } = useGroups();
  const [selected_groups, setSelectedGroups] = useState<Set<UuidString>>(new Set());
  const [group_selector_open, setGroupSelectorOpen] = useState(false);
  const [text_content, setTextContents] = useState<string>('');
  const [media_uploads, setMediaUploads] = useState<UploadedMedia[]>([]);
  const { data: cohort, isLoading: cohort_loading } = useCohort();
  const [mentions, setMentions] = useState<Set<UuidString>>(new Set());
  const [mentionSearchResults, setMentionSearchResults] = useState<FuseResult<SearchableCohort>[] | null>();
  const mentionsInputRef = useRef<HTMLInputElement>(null);
  const [searchableCohort, setSearchableCohort] = useState<SearchableCohort[]>([]);
  const fuse = useMemo(
    () => new Fuse(searchableCohort, { keys: ['aliases.alias'], includeScore: true }),
    [searchableCohort],
  );

  const searchUsers = (searchTerm: string) => {
    if (searchableCohort && searchTerm) {
      setMentionSearchResults(fuse.search(searchTerm));
    } else {
      setMentionSearchResults(null);
    }
  };

  useEffect(() => {
    if (cohort) {
      setSearchableCohort(
        Array.from(cohort, ([id, value]) => {
          return { aliases: value, user_id: id };
        }),
      );
    }
    return () => {};
  }, [cohort]);

  const createPost = async () => {
    const mediaIds = media_uploads
      .map((mu) => {
        return mu.uuid;
      })
      .filter((mu) => mu !== null && mu !== undefined) as UuidString[];

    await peep_client.posts.create_post({
      text_content,
      to_groups: selected_groups,
      mentioned_users: new Set<UuidString>(mentions),
      media: new Set<UuidString>(mediaIds),
    });

    navigate('/feed');
  };

  const addMention = (mention: UuidString) => {
    setMentions(
      produce((previous) => {
        previous.add(mention);
      }),
    );
    const input = mentionsInputRef.current;
    if (input) {
      input.value = '';
    }
    setMentionSearchResults(null);
  };

  return (
    <div
      className={'CreatePost'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <Header pageName="Create Post" />
      <div
        style={{
          width: 'var(--global-content-width)',
          flex: 1,
          minHeight: 0,
          borderRadius: '5px',
          boxShadow: '0px 0px 7px 1px rgba(0 0 0 / 5%)',
          backgroundColor: 'white',
          margin: '1rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* group selector display */}
        <div
          onClick={() => {
            setGroupSelectorOpen(true);
          }}
          style={{ width: '90%', display: 'flex', flexDirection: 'column', margin: '1rem 0 0 0', cursor: 'pointer' }}
        >
          <p style={{ margin: '0' }}>Groups</p>
          <div
            style={{
              borderRadius: '5px',
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: 'var(--global-gray-lighter-3)',
              display: 'flex',
              flexWrap: 'wrap',
              minHeight: '1.15rem',
            }}
          >
            {groups
              ?.filter((g) => selected_groups.has(g.id))
              .map((group, i) => {
                return (
                  <Fragment key={i}>
                    <Badge image_source="/assets/img/new_logo.svg" display_name={group.display_name} />
                  </Fragment>
                );
              })}
          </div>
        </div>

        {/* group selector */}
        {(() => {
          if (group_selector_open) {
            return (
              <Modal
                onClose={() => {
                  setGroupSelectorOpen(false);
                }}
              >
                <h2>Select groups</h2>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'scroll',
                    overflowX: 'clip',
                    alignItems: 'center',
                    height: '60%',
                  }}
                >
                  {groups?.map((group, i) => {
                    return (
                      <Fragment key={i}>
                        <Badge
                          color="var(--global-secondary-lighter-9)"
                          border_color={
                            selected_groups.has(group.id)
                              ? 'var(--global-secondary-lighter-4)'
                              : 'var(--global-secondary-lighter-9)'
                          }
                          height="2rem"
                          width="15rem"
                          image_source="/assets/img/peep_logo.jpg"
                          display_name={group.display_name}
                          onClick={() => {
                            if (selected_groups.has(group.id)) {
                              const new_selected_groups = new Set([...selected_groups]);
                              new_selected_groups.delete(group.id);
                              setSelectedGroups(new_selected_groups);
                            } else {
                              setSelectedGroups(new Set([...selected_groups, group.id]));
                            }
                          }}
                        />
                      </Fragment>
                    );
                  })}
                </div>
              </Modal>
            );
          } else {
            return null;
          }
        })()}

        {/* mentions selector */}
        <div
          style={{ width: '90%', display: 'flex', flexDirection: 'column', margin: '1rem 0 0 0', position: 'relative' }}
          onClick={() => {
            mentionsInputRef.current?.focus();
          }}
        >
          <p style={{ margin: '0' }}>Mentions</p>
          <div
            style={{
              borderRadius: '5px',
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: 'var(--global-gray-lighter-3)',
              display: 'flex',
              flexWrap: 'wrap',
            }}
          >
            {Array.from(mentions).map((mention: UuidString) => {
              const display_name = cohort?.get(mention)?.[0].alias;
              return (
                <div key={mention.toString()}>
                  <Badge
                    image_source="/assets/img/new_logo.svg"
                    display_name={display_name ? display_name : 'unknown'}
                  />
                </div>
              );
            })}
            <input
              ref={mentionsInputRef}
              style={{
                border: 'none',
                flexGrow: 0,
                outline: 'none',
              }}
              onChange={(e) => {
                searchUsers(e.target.value);
              }}
            ></input>
          </div>

          {(() => {
            if (mentionSearchResults) {
              return (
                <div
                  style={{
                    position: 'absolute',
                    width: '100%',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    backgroundColor: 'white',
                    borderColor: 'var(--global-gray-lighter-3)',
                    top: '2.5rem',
                    maxHeight: '10rem',
                    overflowY: 'scroll',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {mentionSearchResults.map((user) => {
                    return (
                      <div
                        key={user.item.user_id.toString()}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          borderBottom: '1px solid var(--global-gray-lighter-3)',
                          width: '100%',
                        }}
                        onClick={(event) => {
                          addMention(user.item.user_id);
                        }}
                      >
                        {user.item.aliases.map((group_alias: alias) => {
                          return (
                            <div key={group_alias.group_id.toString()}>
                              <Badge image_source="/assets/img/new_logo.svg" display_name={group_alias.alias} />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            } else {
              return null;
            }
          })()}
        </div>

        {/* post text input */}
        <div style={{ width: '90%', display: 'flex', flexDirection: 'column', margin: '1rem 0 0 0' }}>
          <p style={{ margin: '0' }}>Text Content</p>
          <textarea
            style={{
              resize: 'none',
              borderRadius: '5px',
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: 'var(--global-gray-lighter-3)',
            }}
            name="Text1"
            cols={40}
            rows={6}
            onChange={(e) => {
              setTextContents(e.target.value);
            }}
          ></textarea>
        </div>

        {/* File Upload */}
        <MediaUploader media_uploads={media_uploads} setMediaUploads={setMediaUploads} />

        {/* Submit */}
        <button
          style={{
            width: '90%',
            margin: 'auto 0 1rem 0',
            height: '2rem',
            background: 'var(--global-main)',
            border: 'none',
            color: 'white',
            textAlign: 'center',
            lineHeight: '2rem',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
          onClick={() => {
            createPost();
          }}
          type="submit"
        >
          POST
        </button>
      </div>
    </div>
  );
}
